// Google Drive → Knowledge Base import.
//
// The browser's only job here is to let the user PICK files; it never touches
// file bytes. The old flow fetched `drive/v3/files/{id}?alt=media` client-side,
// which returns 403 for every Google-native Doc/Sheet/Slide (they have no binary
// content and must be exported) — and because the response was never checked, the
// 403 JSON body was uploaded as the "file" and the progress modal hung forever.
//
// Now we post the picked ids to the backend, which downloads/exports them with the
// user's stored OAuth credentials and runs them through the very same
// KnowledgeBaseUploadView a device upload hits. The response shape is identical to
// the device upload, so KnowledgeBase.jsx feeds both through one code path.

import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import api from "../../chat/api";

const PICKER_SDK_SRC = "https://apis.google.com/js/api.js";

// Mime types the KB pipeline can actually ingest. Restricting the picker means the
// user cannot select something that is guaranteed to fail server-side.
const PICKABLE_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  "application/x-zip-compressed",
  // Google natives — exported server-side to docx/xlsx/pdf.
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.drawing",
  "application/vnd.google-apps.folder",
].join(",");

// Load the Picker SDK once per page, shared across every hook instance.
let pickerLoaderPromise = null;

const loadPickerSdk = () => {
  if (window.google?.picker) return Promise.resolve();
  if (pickerLoaderPromise) return pickerLoaderPromise;

  pickerLoaderPromise = new Promise((resolve, reject) => {
    const onScriptReady = () => {
      if (!window.gapi) {
        reject(new Error("Google API script loaded without gapi"));
        return;
      }
      window.gapi.load("picker", {
        callback: () => resolve(),
        onerror: () => reject(new Error("Failed to load the Google Picker")),
      });
    };

    const existing = document.querySelector(`script[src="${PICKER_SDK_SRC}"]`);
    if (existing) {
      if (window.gapi) onScriptReady();
      else {
        existing.addEventListener("load", onScriptReady);
        existing.addEventListener("error", () =>
          reject(new Error("Failed to load the Google Picker"))
        );
      }
      return;
    }

    const script = document.createElement("script");
    script.src = PICKER_SDK_SRC;
    script.async = true;
    script.defer = true;
    script.onload = onScriptReady;
    script.onerror = () => reject(new Error("Failed to load the Google Picker"));
    document.body.appendChild(script);
  }).catch((err) => {
    pickerLoaderPromise = null; // allow a retry on the next attempt
    throw err;
  });

  return pickerLoaderPromise;
};

/**
 * @param {(fileIds: string[], picked: Array) => Promise<void>} onFilesPicked
 *   Called with the selected Drive file ids once the picker closes.
 */
export default function useKbGoogleDrive(onFilesPicked) {
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [isOpeningPicker, setIsOpeningPicker] = useState(false);

  const isMountedRef = useRef(true);
  const popupRef = useRef(null);
  const popupTimerRef = useRef(null);
  // Ref, not the state flag: two clicks in the same render tick would both read a
  // stale `false` from state and open two pickers.
  const openingRef = useRef(false);
  // Keeps the picker callback pointed at the latest handler without rebuilding
  // the picker, so it always sees current folder/org state.
  const onFilesPickedRef = useRef(onFilesPicked);

  useEffect(() => {
    onFilesPickedRef.current = onFilesPicked;
  }, [onFilesPicked]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (popupTimerRef.current) clearInterval(popupTimerRef.current);
    };
  }, []);

  useEffect(() => {
    loadPickerSdk()
      .then(() => {
        if (isMountedRef.current) setPickerLoaded(true);
      })
      .catch(() => {
        /* surfaced with a toast at click time, not on page load */
      });
  }, []);

  // Cheap connection probe. Unlike GET /google-drive/, this does not list the
  // user's Drive, so it is safe to call on every mount.
  const verifyDriveConnection = useCallback(async () => {
    try {
      const res = await api.get("/api/v1/accounts/google-drive/picker-config/");
      const connected = !!res.data?.connected;
      if (isMountedRef.current) setIsDriveConnected(connected);
      return connected;
    } catch {
      if (isMountedRef.current) setIsDriveConnected(false);
      return false;
    }
  }, []);

  const handleConnectDrive = useCallback(async () => {
    let connectToast;
    try {
      const res = await api.post("/api/v1/accounts/google-drive/");
      const authUrl = res.data?.authorization_url;
      if (!authUrl) {
        toast.error("Could not start Google Drive authorization. Please try again.");
        return;
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        authUrl,
        "GoogleDriveConnect",
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
      );

      if (!popup) {
        toast.error("Please allow popups for this site to connect Google Drive.");
        return;
      }
      popupRef.current = popup;
      connectToast = toast.loading("Connecting to Google Drive…");

      // Settle exactly once, whichever signal arrives first: the callback page's
      // postMessage, or the user closing the popup. The old hook registered two
      // listeners AND reloaded the page, which double-toasted and threw away
      // in-flight uploads.
      let settled = false;
      const finish = async (viaMessage) => {
        if (settled) return;
        settled = true;
        window.removeEventListener("message", handleMessage);
        if (popupTimerRef.current) {
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;
        }
        try {
          if (viaMessage && popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
        } catch {
          /* cross-origin popups can refuse close(); harmless */
        }
        popupRef.current = null;

        const connected = await verifyDriveConnection();
        if (connectToast) toast.dismiss(connectToast);
        if (!isMountedRef.current) return;
        if (connected) toast.success("Google Drive connected.");
        else if (viaMessage) toast.error("Google Drive connection could not be verified.");
        // Popup closed without connecting is almost always a deliberate cancel —
        // stay quiet rather than scolding the user.
      };

      const handleMessage = (event) => {
        // The callback page is served by our own API host, so only trust it.
        const apiOrigin = (() => {
          try {
            return new URL(process.env.REACT_APP_API_URL).origin;
          } catch {
            return null;
          }
        })();
        if (apiOrigin && event.origin !== apiOrigin) return;
        if (event.data?.service === "google_drive" && event.data?.connected) {
          finish(true);
        }
      };

      window.addEventListener("message", handleMessage);
      popupTimerRef.current = setInterval(() => {
        if (popupRef.current?.closed) finish(false);
      }, 1000);
    } catch (err) {
      if (connectToast) toast.dismiss(connectToast);
      console.error("Google Drive connect failed:", err);
      toast.error("Failed to connect Google Drive. Please try again.");
    }
  }, [verifyDriveConnection]);

  const openPicker = useCallback(async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    setIsOpeningPicker(true);
    try {
      const res = await api.get("/api/v1/accounts/google-drive/picker-config/");
      if (!res.data?.connected) {
        setIsDriveConnected(false);
        toast.error(res.data?.message || "Please connect Google Drive first.");
        return;
      }
      setIsDriveConnected(true);

      const { access_token: accessToken, developer_key: developerKey, app_id: appId } = res.data;
      if (!accessToken) {
        toast.error("Google Drive access expired. Please reconnect.");
        setIsDriveConnected(false);
        return;
      }

      try {
        await loadPickerSdk();
      } catch {
        toast.error("Google Picker could not load. Check your connection and retry.");
        return;
      }
      if (!isMountedRef.current) return;
      setPickerLoaded(true);

      const { picker: gPicker } = window.google;

      // Apply optional view/builder options defensively: the Picker SDK is loaded
      // from Google at runtime, so a method disappearing in a future build would
      // otherwise throw mid-chain and the picker would never open at all.
      const apply = (target, method, ...args) => {
        if (typeof target?.[method] === "function") {
          try {
            return target[method](...args) || target;
          } catch (err) {
            console.warn(`Google Picker: ${method} rejected`, err);
          }
        }
        return target;
      };

      const buildView = (ownedByMe, label) => {
        let view = new gPicker.DocsView(gPicker.ViewId.DOCS);
        view = apply(view, "setIncludeFolders", true);
        view = apply(view, "setSelectFolderEnabled", true);
        view = apply(view, "setMimeTypes", PICKABLE_MIME_TYPES);
        if (ownedByMe !== undefined) view = apply(view, "setOwnedByMe", ownedByMe);
        if (label) view = apply(view, "setLabel", label);
        return view;
      };

      let builder = new gPicker.PickerBuilder()
        .addView(buildView(undefined, null))
        .addView(buildView(false, "Shared with me"))
        .enableFeature(gPicker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .setCallback((data) => {
          if (data.action !== gPicker.Action.PICKED) return;
          const docs = data.docs || [];
          const ids = docs.map((d) => d.id).filter(Boolean);
          if (!ids.length) return;
          onFilesPickedRef.current?.(ids, docs);
        });

      builder = apply(builder, "setTitle", "Select files to add to your Knowledge Base");
      if (developerKey) builder = apply(builder, "setDeveloperKey", developerKey);
      if (appId) builder = apply(builder, "setAppId", appId);

      builder.build().setVisible(true);
    } catch (err) {
      console.error("Failed to open Google Picker:", err);
      if (err?.response?.status === 401) {
        setIsDriveConnected(false);
        toast.error("Google Drive access expired. Please reconnect.");
      } else {
        toast.error("Could not open Google Drive. Please try again.");
      }
    } finally {
      openingRef.current = false;
      if (isMountedRef.current) setIsOpeningPicker(false);
    }
  }, []);

  return {
    isDriveConnected,
    pickerLoaded,
    isOpeningPicker,
    verifyDriveConnection,
    handleConnectDrive,
    openPicker,
  };
}
